import {TextInput} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useMemo, useState} from 'react';

import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ClickableIcon, {ClickableIconStyle} from 'ui-library/icons/ClickableIcon';

export interface EnzymeData {
  id: number;
  name: string;
}

export interface EnzymeListEditorProps {
  /** All available enzymes */
  enzymes: Array<EnzymeData>;
  /** IDs of enzymes currently selected in the list */
  selectedEnzymeIds: Array<number>;
  /** If the user can view but not edit this list */
  isReadOnly: boolean;
  /** Called when the user wants to save their changes */
  onSave: (selectedEnzymeIds: Array<number>) => Promise<void>;
}

export default function EnzymeListEditor({
  enzymes,
  selectedEnzymeIds: initialSelectedIds,
  isReadOnly,
  onSave,
}: EnzymeListEditorProps): JSX.Element {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set(initialSelectedIds));
  const [searchQuery, setSearchQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const enzymeMap = useMemo(() => {
    const map = new Map<number, EnzymeData>();
    for (const enzyme of enzymes) {
      map.set(enzyme.id, enzyme);
    }
    return map;
  }, [enzymes]);

  const selectedEnzymes = useMemo(() => {
    const selected: Array<EnzymeData> = [];
    for (const id of selectedIds) {
      const enzyme = enzymeMap.get(id);
      if (enzyme) {
        selected.push(enzyme);
      }
    }
    return selected.sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedIds, enzymeMap]);

  const filteredEnzymes = useMemo(() => {
    if (!searchQuery.trim()) {
      return enzymes;
    }
    const queryParts = searchQuery.toLowerCase().split(' ');
    return enzymes.filter((enzyme) => {
      const name = enzyme.name.toLowerCase();
      return queryParts.every((part) => name.includes(part));
    });
  }, [enzymes, searchQuery]);

  const handleToggleEnzyme = useCallback(
    (enzymeId: number) => {
      if (isReadOnly) {
        return;
      }
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(enzymeId)) {
          next.delete(enzymeId);
        } else {
          next.add(enzymeId);
        }
        return next;
      });
    },
    [isReadOnly]
  );

  const handleRemoveEnzyme = useCallback(
    (enzymeId: number) => {
      if (isReadOnly) {
        return;
      }
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(enzymeId);
        return next;
      });
    },
    [isReadOnly]
  );

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await onSave(Array.from(selectedIds));
    } finally {
      setIsSaving(false);
    }
  }, [onSave, selectedIds]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  return (
    <div>
      <Row>
        <Col sm={6}>
          <h3>Selected enzymes</h3>
          <div className={css(styles.enzymesListContainer)}>
            <table
              className={classNames('table table-hover table-condensed', css(styles.enzymeTable))}
              {...getTestProps({component: 'EnzymeListEditor', element: 'selected-enzymes-table'})}
            >
              <tbody>
                {selectedEnzymes.length > 0 ? (
                  selectedEnzymes.map((enzyme) => (
                    <tr key={enzyme.id}>
                      <td className={css(styles.mono)}>{enzyme.name}</td>
                      {!isReadOnly && (
                        <td className={css(styles.remove)}>
                          <ClickableIcon
                            icon="cross-thick"
                            altText={`Remove ${enzyme.name}`}
                            iconStyle={ClickableIconStyle.NEGATIVE}
                            onClick={() => {
                              handleRemoveEnzyme(enzyme.id);
                            }}
                            className={css(styles.removeIcon)}
                            dataTestAttrs={{
                              component: 'EnzymeListEditor',
                              element: 'remove-enzyme-button',
                              key: enzyme.id.toString(),
                            }}
                          />
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td>
                      <p>
                        <em>
                          {isReadOnly
                            ? 'This list is empty.'
                            : 'This list is empty - add enzymes by clicking on them from the right.'}
                        </em>
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Col>
        <Col sm={6}>
          {isReadOnly && (
            <div
              className={css(styles.readOnlyOverlay)}
              title="Adding enzymes is disabled on read-only lists"
            />
          )}
          <h3>All enzymes</h3>
          <TextInput
            placeholder="Search by name"
            value={searchQuery}
            onChange={handleSearchChange}
            disabled={isReadOnly}
          />
          <div className={css(styles.commonEnzymesContainer)}>
            <table
              className={classNames('table table-hover table-condensed', css(styles.enzymeTable))}
              {...getTestProps({component: 'EnzymeListEditor', element: 'all-enzymes-table'})}
            >
              <tbody>
                {filteredEnzymes.map((enzyme) => {
                  const isSelected = selectedIds.has(enzyme.id);
                  return (
                    <tr
                      key={enzyme.id}
                      onClick={() => {
                        handleToggleEnzyme(enzyme.id);
                      }}
                      role="button"
                      aria-pressed={isSelected}
                    >
                      <td className={classNames([css(styles.mono), {[css(styles.activeRow)]: isSelected}])}>
                        {enzyme.name}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Col>
      </Row>
      {!isReadOnly && (
        <Row>
          <Col sm={12}>
            <Button
              className={css(styles.saveButton)}
              buttonStyle={ButtonStyle.PRIMARY}
              size={ButtonSize.SMALL}
              text={isSaving ? 'Saving...' : 'Save Changes'}
              onClick={handleSave}
              disabled={isSaving}
            />
          </Col>
        </Row>
      )}
    </div>
  );
}

const ACTIVE_COLOR = '#FBD486';
const COMMON_ENZYMES_HEIGHT = 255;

const styles = StyleSheet.create({
  enzymesListContainer: {
    height: COMMON_ENZYMES_HEIGHT + 38,
    overflowY: 'auto',
    paddingRight: 5,
    marginBottom: 10,
  },
  commonEnzymesContainer: {
    height: COMMON_ENZYMES_HEIGHT,
    overflowY: 'auto',
    paddingRight: 5,
    marginBottom: 10,
  },
  enzymeTable: {
    marginBottom: 10,
    width: '100%',
  },
  activeRow: {
    backgroundColor: ACTIVE_COLOR,
  },
  mono: {
    fontFamily: FONT_FAMILY_MONOSPACED,
    fontSize: 13,
    height: 31,
    padding: '5px 10px',
  },
  remove: {
    textAlign: 'right',
  },
  removeIcon: {
    width: 8,
    height: 8,
    ':focus': {
      outline: 'none',
    },
  },
  readOnlyOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    cursor: 'not-allowed',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
  saveButton: {
    float: 'right',
  },
});
